import { useEffect, useState, type ReactNode } from 'react'
import { motion } from 'motion/react'
import { FileIcon, Loader2Icon, ExternalLinkIcon, XIcon } from 'lucide-react'
import type { MoodboardItem, MoodboardPolarity } from '@backend/lib/db/schema/moodboard'
import { fontFamilyFromFilename } from '@/lib/fontFamily'
import { PdfPreview } from '@/components/ui/PdfPreview'
import { MarkdownContent } from '@/components/ui/markdown'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/shadcn/dialog'
import { useCanvasStore } from '../canvas-store'
import { useDragInteraction } from '../use-drag-interaction'
import { SNAP_CURVE } from '../motion-tokens'
import { isMarkdownFile, isTextLikeMime } from '../item-defaults'
import { EnrichingIndicator } from './enriching-indicator'
import { ItemInfoBadge } from './item-info-badge'
import { ResizeHandle } from './resize-handle'

type FileItem = Extract<MoodboardItem, { type: 'file' }>

type Props = {
  item: FileItem
  allItems: MoodboardItem[]
  onMove: (position: { x: number; y: number }) => Promise<void> | void
  onResize: (size: { w: number; h: number }) => Promise<void> | void
  onMetaChange: (patch: {
    polarity?: MoodboardPolarity | undefined
    annotation?: string | undefined
  }) => Promise<void> | void
}

type FileKind =
  | 'image'
  | 'pdf'
  | 'video'
  | 'font'
  | 'audio'
  | 'html'
  | 'markdown'
  | 'text'
  | 'other'

function classifyFile(mime: string, name: string): FileKind {
  if (mime.startsWith('image/')) return 'image'
  if (mime === 'application/pdf') return 'pdf'
  if (mime.startsWith('video/')) return 'video'
  if (mime.startsWith('audio/')) return 'audio'
  if (mime.startsWith('font/') || mime.includes('font')) return 'font'
  // HTML gets its own kind so we can render it visually (sandboxed) instead of
  // showing source. Lives ahead of the generic text branch.
  if (mime === 'text/html' || mime === 'application/xhtml+xml') return 'html'
  // Markdown renders formatted; check before the generic text branch.
  if (isMarkdownFile(mime, name)) return 'markdown'
  if (isTextLikeMime(mime)) return 'text'
  return 'other'
}

// Minimal card — content fills the entire surface. Chrome (info badge, selection ring)
// only appears on hover / select. Drag works everywhere on the card; videos play as
// muted looping previews so they don't capture pointer events for play/pause.

export function FileCard({ item, allItems, onMove, onResize, onMetaChange }: Props) {
  const override = useCanvasStore((s) => s.dragOverrides[item.id])
  const resizeOverride = useCanvasStore((s) => s.resizeOverrides[item.id])
  const isDragging = !!override
  const position = override?.position ?? item.position
  const size = resizeOverride?.size ?? item.size
  const isSelected = useCanvasStore((s) => s.selectedIds.has(item.id))
  const [viewerOpen, setViewerOpen] = useState(false)
  const drag = useDragInteraction({
    itemId: item.id,
    basePosition: item.position,
    linkedItems: allItems,
    onCommit: onMove,
    onSelect: (e) => useCanvasStore.getState().selectFromPointer(item.id, e.shiftKey),
  })
  const kind = classifyFile(item.file.file.type || '', item.file.file.name)

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.24, ease: SNAP_CURVE }}
      className="group absolute"
      style={{
        left: position.x,
        top: position.y,
        width: size.w,
        height: size.h,
      }}>
      <div
        {...drag}
        // Shift is reserved for multi-select toggling; don't open the viewer on a
        // shift-double-click (a fast shift-click sequence otherwise reads as one).
        onDoubleClick={(e) => {
          if (e.shiftKey) return
          setViewerOpen(true)
        }}
        className={[
          'bg-card ring-border/60 relative h-full w-full overflow-hidden rounded-lg shadow-sm ring-1 transition-shadow',
          isDragging ? 'cursor-grabbing shadow-lg' : 'cursor-grab',
          isSelected ? 'ring-primary ring-offset-background ring-2 ring-offset-2' : '',
        ].join(' ')}>
        <FileBody item={item} kind={kind} />
        <FileViewerDialog item={item} kind={kind} open={viewerOpen} onOpenChange={setViewerOpen} />
        <EnrichingIndicator itemId={item.id} />

        <ItemInfoBadge
          polarity={item.polarity}
          annotation={item.annotation}
          onChange={onMetaChange}>
          <p className="text-foreground truncate font-medium">{item.file.file.name}</p>
          <p className="mt-1">
            {labelForKind(kind)} · {formatBytes(item.file.file.size)}
          </p>
          <p className="mt-1">{item.file.file.type || 'unknown'}</p>
          {kind === 'video' && item.videoAnalysisProse && (
            <details className="border-border/60 mt-2 border-t pt-2">
              <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-[10px] uppercase tracking-wide">
                Video analysis
              </summary>
              <p
                onWheel={(e) => e.stopPropagation()}
                className="text-muted-foreground mt-1.5 max-h-64 overflow-y-auto whitespace-pre-wrap text-[10px] leading-relaxed">
                {item.videoAnalysisProse}
              </p>
            </details>
          )}
        </ItemInfoBadge>

        <ResizeHandle itemId={item.id} baseSize={item.size} onCommit={onResize} />
      </div>
    </motion.div>
  )
}

// Full-size viewer opened by double-clicking a file card. Images and videos
// render uncropped (object-contain) so you see the whole thing; PDFs/HTML get a
// tall iframe; everything else reuses the card body at large size. An "Open
// original" link is always available for true fullscreen / download.
function FileViewerDialog({
  item,
  kind,
  open,
  onOpenChange,
}: {
  item: FileItem
  kind: FileKind
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const url = item.file.url
  const name = item.file.file.name

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Full-screen transparent wrapper: the framed card sits centered inside it, and
        // clicking the wrapper (the dark backdrop around the card) closes the viewer. We
        // don't lean on Radix's outside-click — the dialog is a JSX child of the draggable
        // card, so its events bubble through React's portal into the canvas drag handler;
        // the onPointerDown guard stops that and also defeats Radix's outside-close.
        // `[&>button]:hidden` drops shadcn's default corner X (it would float low-contrast
        // on the backdrop) — the card header carries its own close button instead.
        className="inset-0 flex max-h-none max-w-none translate-x-0 translate-y-0 items-center justify-center border-0 bg-transparent p-6 shadow-none sm:max-w-none sm:rounded-none [&>button]:hidden"
        // Pointer events inside the dialog must not reach the canvas drag handlers.
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          if (e.target === e.currentTarget) onOpenChange(false)
        }}>
        <div className="bg-background ring-border/60 flex max-h-full w-auto max-w-full flex-col overflow-hidden rounded-lg shadow-xl ring-1">
          <div className="border-border/60 flex items-center justify-between gap-3 border-b py-2.5 pl-4 pr-3">
            <DialogTitle className="truncate text-sm font-medium">{name}</DialogTitle>
            <div className="flex shrink-0 items-center gap-3">
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs">
                <ExternalLinkIcon size={13} />
                Open original
              </a>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                aria-label="Close"
                className="text-muted-foreground hover:text-foreground rounded-sm opacity-70 transition-opacity hover:opacity-100">
                <XIcon size={16} />
              </button>
            </div>
          </div>
          <div
            // Clicking the empty letterbox around the media also closes; the currentTarget
            // guard keeps clicks on the media itself from dismissing.
            onClick={(e) => {
              if (e.target === e.currentTarget) onOpenChange(false)
            }}
            className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-black/5 dark:bg-black/30">
            {kind === 'image' && (
              // crossOrigin='anonymous' so this viewer load doesn't poison the browser cache
              // with a non-CORS entry that later breaks the same asset on the workspace canvas.
              <img
                src={url}
                alt={name}
                crossOrigin="anonymous"
                className="max-h-[82vh] max-w-full object-contain"
              />
            )}
            {kind === 'video' && (
              <video src={url} controls autoPlay className="max-h-[82vh] max-w-full" />
            )}
            {kind === 'audio' && (
              <div className="w-full max-w-lg p-8">
                <audio src={url} controls autoPlay className="w-full" />
              </div>
            )}
            {(kind === 'pdf' || kind === 'html') && (
              <iframe
                title={name}
                src={url}
                {...(kind === 'html'
                  ? { sandbox: '' as const, referrerPolicy: 'no-referrer' }
                  : {})}
                className="h-[82vh] w-[88vw] bg-white"
              />
            )}
            {(kind === 'font' || kind === 'markdown' || kind === 'text' || kind === 'other') && (
              <div className="h-[70vh] w-[70vw] max-w-3xl">
                <FileBody item={item} kind={kind} />
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function FileBody({ item, kind }: { item: FileItem; kind: FileKind }) {
  if (kind === 'image') {
    // crossOrigin is required, not cosmetic: this is our S3 asset, and the same URL is
    // later loaded onto the workspace canvas with crossOrigin='anonymous'. If this first
    // load is a plain (non-CORS) request, the browser caches a header-less response that
    // the later CORS load reuses and rejects ("No Access-Control-Allow-Origin"). Loading
    // CORS-mode here keeps the cache entry usable by the canvas. Do not remove.
    return (
      <img
        src={item.file.url}
        alt=""
        crossOrigin="anonymous"
        draggable={false}
        className="h-full w-full object-cover"
      />
    )
  }
  if (kind === 'video') {
    // Muted autoplay loop — no controls so drag works. Acts as a continuous preview
    // (gif-like). Audio + scrubbing are deferred to the info popover / external open.
    return (
      <video
        src={item.file.url}
        autoPlay
        muted
        loop
        playsInline
        className="pointer-events-none h-full w-full bg-black object-cover"
      />
    )
  }
  if (kind === 'audio') {
    return <AudioBody url={item.file.url} mime={item.file.file.type || ''} />
  }
  if (kind === 'html') {
    return <HtmlPreviewBody url={item.file.url} name={item.file.file.name} />
  }
  if (kind === 'markdown') {
    return <MarkdownPreviewBody url={item.file.url} name={item.file.file.name} />
  }
  if (kind === 'text') {
    return <TextPreviewBody url={item.file.url} name={item.file.file.name} />
  }
  if (kind === 'font') {
    // Prefer the parsed family; fall back to the filename-derived name. Matches
    // exactly what the workspace canvas registers and paints.
    const family = item.fontMeta?.family ?? fontFamilyFromFilename(item.file.file.name)
    return <FontSpecimen url={item.file.url} family={family} />
  }
  if (kind === 'pdf') {
    // Wrap PdfPreview so clicks on its nav buttons don't bubble up and start a card drag.
    return (
      <div
        className="h-full w-full"
        onPointerDown={(e) => {
          if ((e.target as HTMLElement).closest('button')) e.stopPropagation()
        }}>
        <PdfPreview
          src={item.file.url}
          thumbnailUrl={item.thumbnailUrl}
          className="h-full w-full"
        />
      </div>
    )
  }
  return (
    <div className="bg-muted flex h-full w-full items-center justify-center">
      <FileIcon className="text-muted-foreground" size={48} strokeWidth={1.5} />
    </div>
  )
}

// Fetches the head of a text file once. Capped at 64KB via a Range request so
// large logs / minified bundles / long docs don't hammer the network.
const TEXT_PREVIEW_BYTES = 64 * 1024
function useTextHead(url: string) {
  const [content, setContent] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    setContent(null)
    setError(null)
    // Try Range first; fall back to full fetch if the host doesn't support it.
    fetch(url, { headers: { Range: `bytes=0-${TEXT_PREVIEW_BYTES - 1}` } })
      .then(async (r) => {
        if (!r.ok && r.status !== 206) throw new Error(`HTTP ${r.status}`)
        return r.text()
      })
      .then((text) => {
        if (cancelled) return
        setContent(text.slice(0, TEXT_PREVIEW_BYTES))
      })
      .catch((e) => {
        if (cancelled) return
        setError(e instanceof Error ? e.message : 'Failed to load')
      })
    return () => {
      cancelled = true
    }
  }, [url])
  return { content, error }
}

// Shared chrome: filename header + centered loader / error states.
function TextFileFrame({
  name,
  content,
  error,
  children,
}: {
  name: string
  content: string | null
  error: string | null
  children: (content: string) => ReactNode
}) {
  return (
    <div className="bg-card flex h-full w-full flex-col overflow-hidden">
      <div className="border-border/60 text-muted-foreground flex items-center justify-between border-b px-3 py-1.5 text-[10px] uppercase tracking-wide">
        <span className="truncate font-medium">{name}</span>
      </div>
      <div className="relative flex-1 overflow-hidden">
        {content === null && !error && (
          <div className="text-muted-foreground absolute inset-0 flex items-center justify-center">
            <Loader2Icon size={16} className="animate-spin" />
          </div>
        )}
        {error && (
          <div className="text-muted-foreground absolute inset-0 flex items-center justify-center text-xs">
            {error}
          </div>
        )}
        {content !== null && children(content)}
      </div>
    </div>
  )
}

function TextPreviewBody({ url, name }: { url: string; name: string }) {
  const { content, error } = useTextHead(url)
  return (
    <TextFileFrame name={name} content={content} error={error}>
      {(text) => (
        <>
          <pre className="text-foreground/80 absolute inset-0 overflow-hidden whitespace-pre-wrap break-words p-3 font-mono text-[10px] leading-relaxed">
            {text}
          </pre>
          <div className="from-card pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t to-transparent" />
        </>
      )}
    </TextFileFrame>
  )
}

// Soften Streamdown's default inline-code chips for the dense preview: it fills
// them with a solid `bg-muted` and sizes them `text-sm` (larger than our text-xs
// body, so they bulge). Swap to a faint foreground tint that adapts to both
// themes and a relative font size so chips sit inline instead of standing out.
const INLINE_CODE_OVERRIDES = [
  '[&_[data-streamdown=inline-code]]:!bg-foreground/[0.06]',
  '[&_[data-streamdown=inline-code]]:!text-foreground/90',
  '[&_[data-streamdown=inline-code]]:!text-[0.85em]',
  '[&_[data-streamdown=inline-code]]:!font-normal',
].join(' ')

// Renders markdown as formatted rich text (headings, lists, code, links) instead
// of raw source. Scrolls internally; wheel events are stopped so the canvas
// doesn't pan while reading.
function MarkdownPreviewBody({ url, name }: { url: string; name: string }) {
  const { content, error } = useTextHead(url)
  return (
    <TextFileFrame name={name} content={content} error={error}>
      {(text) => (
        <>
          <div
            onWheel={(e) => e.stopPropagation()}
            className={`chat-markdown absolute inset-0 overflow-y-auto p-3 text-xs leading-relaxed ${INLINE_CODE_OVERRIDES}`}>
            <MarkdownContent>{text}</MarkdownContent>
          </div>
          <div className="from-card pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t to-transparent" />
        </>
      )}
    </TextFileFrame>
  )
}

// Renders HTML as actual HTML inside a sandboxed iframe. `sandbox=""` disables
// scripts, forms, popups, top-navigation, plugins — the file is safe even if its
// author put `<script>` tags or malicious onclick handlers in it. Pointer events
// are disabled on the iframe so card drag still works from anywhere on the card.
function HtmlPreviewBody({ url, name }: { url: string; name: string }) {
  return (
    <div className="bg-card flex h-full w-full flex-col overflow-hidden">
      <div className="border-border/60 text-muted-foreground flex items-center justify-between border-b px-3 py-1.5 text-[10px] uppercase tracking-wide">
        <span className="truncate font-medium">{name}</span>
      </div>
      <iframe
        title={name}
        src={url}
        sandbox=""
        referrerPolicy="no-referrer"
        className="pointer-events-none h-full w-full flex-1 bg-white"
      />
    </div>
  )
}

function AudioBody({ url, mime }: { url: string; mime: string }) {
  return (
    <div className="bg-muted flex h-full w-full items-center justify-center p-3">
      <audio
        src={url}
        controls
        preload="metadata"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-full"
        // Hint MIME for browsers that infer poorly from URL extension.
        data-mime={mime}
      />
    </div>
  )
}

function FontSpecimen({ url, family }: { url: string; family: string }) {
  // `family` is the canonical CSS family (parsed name when available, else the
  // filename-derived name) — matches what the workspace canvas registers.
  return (
    <div className="bg-card flex h-full w-full items-center justify-center overflow-hidden p-3">
      <style>
        {`@font-face { font-family: "${family}"; src: url("${url}"); font-display: swap; }`}
      </style>
      <span
        className="text-foreground truncate text-3xl"
        style={{ fontFamily: `"${family}", system-ui` }}>
        Aa Bb Cc
      </span>
    </div>
  )
}

function labelForKind(kind: FileKind): string {
  if (kind === 'image') return 'Image'
  if (kind === 'pdf') return 'PDF'
  if (kind === 'video') return 'Video'
  if (kind === 'font') return 'Font'
  if (kind === 'audio') return 'Audio'
  if (kind === 'html') return 'HTML'
  if (kind === 'markdown') return 'Markdown'
  if (kind === 'text') return 'Text'
  return 'File'
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
